/**
 * Approval Banner
 *
 * Floating sticky banner shown at top of sections when AI changes are ready for approval.
 */

import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Box, Button, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { LAYOUT_Z_INDEX } from '../constants/layout';

import { StickyBanner } from './primitives/StickyBanner';

interface ApprovalBannerProps {
  /**
   * Whether the banner is visible
   */
  isVisible: boolean;

  /**
   * Summary message from AI about the changes
   */
  message?: string;

  /**
   * Number of sections affected by changes
   */
  affectedCount: number;

  /**
   * Callback when user approves changes
   */
  onApprove: () => void;

  /**
   * Callback when user rejects changes
   */
  onReject: () => void;

  /**
   * Whether approval is being processed
   */
  isProcessing?: boolean;

  /**
   * Optional label for confirm/approve button
   */
  confirmLabel?: string;

  /**
   * Optional label for reject/cancel button
   */
  cancelLabel?: string;

  /**
   * Navigation controls for affected sections
   */
  navigation?: {
    currentIndex: number;
    totalCount: number;
    isFirst: boolean;
    isLast: boolean;
    scrollToNext: () => void;
    scrollToPrevious: () => void;
    scrollToFirst: () => void;
  };
}

export const ApprovalBanner: React.FC<ApprovalBannerProps> = ({
  isVisible,
  message = 'AI has suggested changes to your note sections.',
  affectedCount,
  onApprove,
  onReject,
  isProcessing = false,
  confirmLabel = 'Approve',
  cancelLabel = 'Reject',
  navigation,
}) => {
  const theme = useTheme();
  const { noteId, encounterId } = useParams<{ noteId: string; encounterId: string }>();
  const { note: noteMetrics } = useEventTracking();
  const bannerShownTrackedRef = useRef<boolean>(false);

  // Track when approval banner becomes visible
  useEffect(() => {
    if (isVisible && !bannerShownTrackedRef.current && noteId) {
      bannerShownTrackedRef.current = true;

      noteMetrics.approvalBannerShown?.(noteId, {
        affected_sections_count: affectedCount,
        message,
        note_id: noteId,
        encounter_id: encounterId,
      });
    }

    // Reset tracking flag when banner becomes invisible
    if (!isVisible) {
      bannerShownTrackedRef.current = false;
    }
  }, [isVisible, noteId, encounterId, affectedCount, message, noteMetrics]);

  // Track navigation clicks
  const handleNavigationClick = (direction: 'next' | 'previous', callback: () => void) => {
    if (noteId && navigation) {
      noteMetrics.approvalNavigationClicked?.(noteId, {
        direction,
        current_section_index: navigation.currentIndex,
        total_sections: navigation.totalCount,
        note_id: noteId,
      });
    }
    callback();
  };

  return (
    <StickyBanner
      isVisible={isVisible}
      top={0}
      zIndex={LAYOUT_Z_INDEX.APPROVAL_BANNER}
      animationType="slideDown"
      animationDuration={0.3}
    >
      <Paper
        elevation={4}
        sx={{
          bgcolor: theme.palette.background.paper,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          px: 3,
          py: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: theme.palette.primary.light,
          color: theme.palette.primary.main,
        }}
      >
        <CheckCircleOutlineIcon />
      </Box>

      {/* Content - Clickable to scroll to first section */}
      <Tooltip title={navigation ? 'Click to view first change' : ''} arrow>
        <Box
          sx={{
            flex: 1,
            cursor: navigation ? 'pointer' : 'default',
            '&:hover': navigation
              ? {
                  opacity: 0.9,
                }
              : undefined,
          }}
          onClick={navigation ? navigation.scrollToFirst : undefined}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Review AI Changes
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {message}
            {affectedCount > 0 && (
              <Typography component="span" variant="body2" sx={{fontWeight: 600,  ml: 1 }}>
                ({affectedCount} section{affectedCount === 1 ? '' : 's'} affected)
              </Typography>
            )}
          </Typography>
        </Box>
      </Tooltip>

      {/* Navigation Controls */}
      {navigation && navigation.totalCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderLeft: `1px solid ${theme.palette.divider}`,
            borderRight: `1px solid ${theme.palette.divider}`,
            px: 2,
          }}
        >
          <Tooltip title="Previous section" arrow>
            <IconButton
              size="small"
              onClick={() => handleNavigationClick('previous', navigation.scrollToPrevious)}
              disabled={isProcessing}
              aria-label="Previous affected section"
              sx={{
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light',
                },
              }}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ minWidth: '70px', textAlign: 'center' }}
          >
            Section {navigation.currentIndex + 1} of {navigation.totalCount}
          </Typography>

          <Tooltip title="Next section" arrow>
            <IconButton
              size="small"
              onClick={() => handleNavigationClick('next', navigation.scrollToNext)}
              disabled={isProcessing}
              aria-label="Next affected section"
              sx={{
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light',
                },
              }}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<HighlightOffIcon />}
          onClick={onReject}
          disabled={isProcessing}
          sx={{ minWidth: 120 }}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={onApprove}
          disabled={isProcessing}
          sx={{ minWidth: 120 }}
        >
          {isProcessing ? 'Applying...' : confirmLabel}
        </Button>
      </Box>
    </Paper>
    </StickyBanner>
  );
};
